(ns shared.core.amelia-light.components.button
  (:require [shared.core.components.radio :as radio]
            [shared.core.components.icon :as icon]
            [shared.core.amelia-light.components.icon :as alc-icon]
            [shared.core.amelia-light.components.tooltip :as alc-tooltip]
            [shared.core.components.interactive :refer [interactive]]
            [shared.core.loading :as loading]
            [shared.core.style :as style]
            [shared.core.util :as util]
            ["@up/base-ui/components/ui/button" :refer [Button]]))

(defn base-button [{:keys [style material-icon-type material-icon-style on-click disabled? content-style
                           hover-style loading? loading-config size] :as args}
                   & contents]
  (let [on-click (when-not disabled? on-click)
        large? (= size :large)]
    [interactive
     :div (merge {:style (cond-> {:background-color "#fff"
                                  :border-radius 3
                                  :border "1px solid rgba(0, 0, 0, 0.2)"
                                  :display :flex
                                  :align-items :center
                                  :gap 6
                                  :padding (if material-icon-type
                                             (if (empty? contents)
                                               "3px 4px"
                                               "3px 12px 3px 6px")
                                             "3px 14px")
                                  :transition "0.2s"}
                                 large? (assoc :gap 10
                                               :padding (if material-icon-type
                                                          (if (empty? contents)
                                                            "6px 8px"
                                                            "6px 14px 6px 8px")
                                                          "6px 20px"))
                                 disabled? (assoc :cursor :not-allowed
                                                  :opacity 0.4)
                                 on-click (assoc :cursor :pointer)
                                 style (merge style))
                  :hover-style (when on-click
                                 (merge {:background-color "rgba(0, 0, 0, 0.02)"}
                                        hover-style))
                  :on-click on-click}
                 (dissoc args :style :material-icon-type :on-click :hover-style))
     (when (and material-icon-type (not loading?))
       [icon/material-icon {:icon-type material-icon-type
                            :style (merge {:font-size 14
                                           :color (util/hex->rgba (style/color :black :base) 0.8)}
                                          (when large?
                                            {:font-size 16})
                                          material-icon-style)}])
     (if loading?
       [loading/spinner (merge {:color (or (:color content-style)
                                           (util/hex->rgba (style/color :black :base) 0.8))
                                :size 14}
                               (when large?
                                 {:size 16})
                               loading-config)]
       (when (not-empty contents)
         (into [:span {:style (merge {:color (util/hex->rgba (style/color :black :base) 0.8)
                                      :font-size 12
                                      :font-weight style/font-regular-weight
                                      :line-height 1.5}
                                     (when large?
                                       {:font-size 15})
                                     content-style)}]
               contents)))]))

(defn white-button [args & contents]
  (into [base-button args]
        contents))

(defn white-circle-icon-button [{:keys [material-icon-type material-icon-style] :as args}]
  [white-button (merge {:material-icon-type material-icon-type
                        :material-icon-style (merge {:font-size 16}
                                                    material-icon-style)
                        :style {:border-radius "50%"
                                :box-shadow "0 2px 3px 0 rgba(34,36,38,.15)"
                                :padding 10
                                :transition "0.2s"}
                        :hover-style {:background-color "#F3E9FD"
                                      :box-shadow "0 2px 6px 0 rgba(34,36,38,.25)"}}
                       (dissoc args :material-icon-type :material-icon-style))])

(defn purple-button [{:keys [style material-icon-style content-style] :as args} & contents]
  (into [base-button (merge args
                            {:style (merge {:background-color "#9933F0"
                                            :border "1px solid #9933F0"}
                                           style)
                             :material-icon-style (merge {:color "#fff"}
                                                         material-icon-style)
                             :content-style (merge {:color "#fff"}
                                                   content-style)
                             :hover-style {:background-color "#a952f3"}})]
        contents))

(defn button-toggle
  [{:keys [first? last? border-radius container-style content-style selected-pill-style] :as args} & contents]
  (let [pill-border (str "1px solid " (util/hex->rgba (style/color :black :base) 0.2))]
    (into [radio/pill-radio (merge args
                                   {:border-radius 3
                                    :container-style (merge {:display :flex
                                                             :align-items :center
                                                             :width :auto
                                                             :height 32
                                                             :background-color (style/color :white :base)
                                                             :border-left pill-border
                                                             :border-top pill-border
                                                             :border-bottom pill-border
                                                             :border-right (when last? pill-border)}
                                                            container-style)
                                    :content-style (merge {:display :flex
                                                           :padding "3px 12px"
                                                           :justify-content :center
                                                           :align-items :center
                                                           :background-color (style/color :white :base)}
                                                          content-style)
                                    :selected-pill-style (merge {:height "100%"
                                                                 :background-color (util/hex->rgba "#9933f0" 0.15)
                                                                 :border-top-left-radius (if first? border-radius 0)
                                                                 :border-bottom-left-radius (if first? border-radius 0)
                                                                 :border-top-right-radius (when last? border-radius)
                                                                 :border-bottom-right-radius (when last? border-radius)}
                                                                selected-pill-style)})]
          contents)))

(defn button-group-toggle [{:keys [group-options on-change selected-option style]}]
  (into [:div {:style (merge {:display :flex
                              :align-items :center}
                             style)}]
        (map-indexed (fn [idx {:keys [icon-type text value]}]
                       (let [selected? (= selected-option value)]
                         [button-toggle {:first? (= idx 0)
                                         :last? (= idx (dec (count group-options)))
                                         :selected? selected?
                                         :on-click (when (and on-change (not selected?))
                                                     #(on-change value))}
                          (when icon-type
                            [icon/material-icon {:icon-type icon-type
                                                 :style {:font-size 16
                                                         :color (util/hex->rgba (style/color :black :base) 0.8)
                                                         :margin-right 6}}])
                          [:span {:style {:font-size 12
                                          :line-height 1.5
                                          :font-weight style/font-regular-weight
                                          :color (util/hex->rgba (style/color :black :base) 0.8)}}
                           text]]))
                     group-options)))

(defn up-button [{:keys [icon-type icon rounded? loading? style tooltip-content] :as args} & contents]
  (let [button (into [:> Button (cond-> (-> args
                                            (dissoc :tooltip-content)
                                            (assoc :style (merge (when rounded?
                                                                   {:border-radius "50%"})
                                                                 style)))
                                        loading? (assoc :is-loading true)
                                        icon-type (merge (if (not-empty contents)
                                                           {:class-name "ps-3"}
                                                           {:icon (or icon "md")})))
                      (when icon-type
                        [alc-icon/up-icon {:icon-type icon-type}])]
                     contents)]
    (if tooltip-content
      [alc-tooltip/tooltip {:tooltip-content tooltip-content
                            :target-content button}]
      button)))

(defn up-ghost-button [args & contents]
  (into [up-button (assoc args :variant "ghost")]
        contents))

(defn up-call-to-action-button [args & contents]
  (into [up-button (assoc args :variant "callToAction")]
        contents))

(defn up-toggle-pressed-button [args & contents]
  (into [up-button (assoc args :variant "togglePressed")]
        contents))
